import React, { Fragment } from 'react'

import { Grid, Typography } from '@mui/material'
import { observer } from 'mobx-react'
import { useFormContext, useWatch } from 'react-hook-form'
import ReactPlayer from 'react-player'
import mobilescreen from '../../assets/icons/ic_mobilescreen.svg'
import mobilescreen_active from '../../assets/icons/ic_mobilescreen_active.svg'
import rectangle from '../../assets/icons/ic_rectangle.svg'
import rectangle_43 from '../../assets/icons/ic_rectangle_43.svg'
import rectangle_43_active from '../../assets/icons/ic_rectangle_43_active.svg'
import rectangle_active from '../../assets/icons/ic_rectangle_active.svg'
import { useStrings } from '../../assets/localization/strings'
import { UnboxingType, VideoDuration, VideoFormat, VideoType } from '../../models/GeneralTypes'
import { numberToEuro } from '../../reusableUtils/Helpers'
import { usePricingContext } from '../../reusableUtils/pricing'
import ReactHookFormRadioCardGroupFullImage from '../common/form/ReactHookFormRadioCardGroupFullImage'
import ReactHookFormRadioCardGroupIcon from '../common/form/ReactHookFormRadioCardGroupIcon'
import type { IUGCCampaign } from './usePersistedUGCForm'
import { getCurrencySymbolByCountry } from '../../reusableUtils/currency'
import rootStore from '../../stores/rootStore'
import { getVideoUrl } from '../../config'

// import video from 'sample.mp4'

// {
//   image: 'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/public/webapp_videos/ad_video.mp4',
//   showControls: true,
//   title: 'Video Testimonial',
//   value: VideoType.testimonial,
//   description: 'Ideal für etwas.',
//   price: pricing[VideoType.testimonial],
//   isPriceAdditional: false
// }

const CampaignType = observer(() => {
  const pricing = usePricingContext()
  const strings = useStrings()
  const { control, getValues } = useFormContext<IUGCCampaign>()
  const videoType = useWatch({ control, name: 'videoType' })
  const exampleVideoUrl = getVideoUrl('default', rootStore)
  const premiumExampleVideo = getVideoUrl('premium', rootStore)

  const currencySymbol = rootStore.brandStore.selectedBrand?.brandCountry ? getCurrencySymbolByCountry(rootStore.brandStore.selectedBrand?.brandCountry) : '€'

  const videoDurationOptions = (() => {
    const sec30 = pricing[VideoDuration.Sec30]
    const sec60 = videoType == VideoType.premium ? pricing.sec60Premium : pricing[VideoDuration.Sec60]
    return [
      {
        value: VideoDuration.Sec15,
        title: strings.campaign_creation_video_duration_15,
        description: strings.inclusive,
        descriptionList: ['TikTok-Video', 'Instagram-Video', 'Instagram-Reel', 'Pinterest-Video', 'Snapchat-Ad']

        // descriptionSmall: strings.campaign_creation_video_duration_price(numberToEuro((base + pricing[VideoDuration.Sec15]) / VideoDuration.Sec15))
      },
      {
        value: VideoDuration.Sec30,
        title: strings.campaign_creation_video_duration_30,
        description: `${currencySymbol === '£' ? currencySymbol + numberToEuro(sec30) : numberToEuro(sec30) + currencySymbol}`,
        descriptionList: ['Instagram-Reel', 'TikTok-Ads', 'Snapchat-Ad', 'Live-Shopping', 'Landing-Page'],
        // descriptionSmall: strings.campaign_creation_video_duration_price(numberToEuro((base + sec30) / VideoDuration.Sec30))
        topBadge: strings.popular
      },
      {
        value: VideoDuration.Sec60,
        title: strings.campaign_creation_video_duration_60,
        description: `${currencySymbol === '£' ? currencySymbol + numberToEuro(sec60) : numberToEuro(sec60) + currencySymbol}`,
        descriptionList: ['Facebook-Video', 'Produktvorstellung', 'Testimonials', 'Advertorial', 'Landing-Page']
        // descriptionSmall: strings.campaign_creation_video_duration_price(numberToEuro((base + sec60) / VideoDuration.Sec60))
      }
    ].filterNotNull()
  })()
  const videoTypeOptions = [
    {
      content: exampleVideoUrl,
      showControls: true,
      title: strings.campaign_creation_video_type_ad_title,
      subTitle: strings.campaign_creation_video_type_ad_subtitle,
      value: VideoType.ad,
      description: strings.campaign_creation_video_type_ad_desc,
      price: pricing[VideoType.ad],
      isPriceAdditional: false
    },
    {
      content: premiumExampleVideo,
      showControls: true,
      title: strings.campaign_creation_video_type_premium_title,
      subTitle: strings.campaign_creation_video_type_premium_subtitle,
      value: VideoType.premium,
      description: strings.campaign_creation_video_type_premium_desc,
      price: pricing[VideoType.premium],
      isPriceAdditional: false
    },
    /*     {
          content: 'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/public/webapp_videos/premium_video.mp4',
          showControls: true,
          title: strings.unboxing,
          value: VideoType.unboxing,
          description: strings.unboxing_description,
          price: pricing[VideoType.unboxing],
          isPriceAdditional: false
        } */
  ].filterNotNull()

  const unboxingOptions = [
    {
      content: 'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/public/webapp_videos/UGC+Sockenvideo_V1_1zu1.mov',
      showControls: true,
      title: strings.unboxing_faceless,
      value: UnboxingType.faceless,
      description: strings.unboxing_faceless_description,
      isPriceAdditional: false
    },
    {
      content: 'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/public/webapp_videos/UGC+Kerzenvideo+1zu1.mov',
      showControls: true,
      title: strings.unboxing_creator,
      value: UnboxingType.creator,
      description: strings.unboxing_creator_description,
      isPriceAdditional: false
    },
    {
      content: 'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/business-webapp/Unboxing+UGc.mp4',
      showControls: true,
      title: strings.unboxing_creator,
      value: UnboxingType.creator,
      description: strings.unboxing_creator_description,
      isPriceAdditional: false
    }
  ].filterNotNull()

  const videoFormatOptions = [
    {
      image: mobilescreen,
      imageActive: mobilescreen_active,
      value: VideoFormat.vertical,
      description: strings.campaign_creation_video_format_vertical,
      title: '9:16',
      descriptionSmall: strings.campaign_creation_video_format_vertical_platform,
      iconDimensions: {
        width: '60px',
        height: '106px' // 106.67px correct precised size
      }
    },
    {
      image: rectangle,
      imageActive: rectangle_active,
      value: VideoFormat.horizontal,
      description: strings.campaign_creation_video_format_horizontal,
      title: '16:9',
      descriptionSmall: strings.campaign_creation_video_format_horizontal_platform,
      iconDimensions: {
        width: '126px', // 106.67px correct precised size
        height: '60px'
      }
    },
    {
      image: rectangle_43,
      imageActive: rectangle_43_active,
      value: VideoFormat.fullscreen,
      description: strings.campaign_creation_video_format_fullscreen,
      title: '4:3',
      descriptionSmall: strings.campaign_creation_video_format_fullscreen_platform,
      iconDimensions: {
        width: '80px',
        height: '60px'
      }
    }
  ]

  const selectedVideoTypeVideo = videoTypeOptions.find(t => t.value === videoType)?.content
  const selectedUnboxingTypeVideo = unboxingOptions.map(t => t.content)

  const selectedVideoType = getValues('videoType')
  const isUnboxing = selectedVideoType == VideoType.unboxing
  return (
    <Grid container sx={{ padding: '24px', marginBottom: '22px', borderStyle: 'solid', borderRadius: '16px', borderColor: '#17181B33', borderWidth: '1px' }}>
      <Grid item xs={12}>
        <Typography sx={{ fontSize: '18px', fontWeight: 500, fontFamily: 'Bricolage Grotesque' }}>{strings.campaign_creation_video_type_title}</Typography>
      </Grid>
      <Grid item xs={12} mt={2}>
        <ReactHookFormRadioCardGroupFullImage name='videoType' defaultValue={VideoType.ad} options={videoTypeOptions} videosOnBottom required md={6} />
      </Grid>
      {isUnboxing && (
        <Fragment>
          <Grid item xs={12} mt={2}>
            <ReactHookFormRadioCardGroupFullImage name='unboxingType' md={6} defaultValue={UnboxingType.faceless} options={unboxingOptions} videosOnBottom required />
          </Grid>
        </Fragment>
      )}
      {/* exmaple video: shows up only for unboxing type  */}
      {!!selectedVideoTypeVideo && isUnboxing && (
        <>
          <Grid item xs={12} mt={'20px'}>
            <Typography sx={{ fontSize: '18px', fontWeight: 500, fontFamily: 'Bricolage Grotesque', mt: '20px' }}>{strings.campaign_creation_video_type_example_title}</Typography>
          </Grid>
          <Grid item sx={{ display: 'flex', justifyContent: 'space-between' }} xs={12} mt={2}>
            {selectedUnboxingTypeVideo.map((video, i) => (
              <Grid key={i} item xs={12} mt={2} sx={{ display: 'flex', justifyContent: 'center' }}>
                <span className='videoPlayerWrapper roundedVideo'>
                  <ReactPlayer url={video} height='auto' width='auto' controls={true} />
                </span>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {!isUnboxing && (
        <Fragment>
          <Grid item xs={12} mt={2}>
            <Typography sx={{ fontSize: '18px', fontWeight: 500, fontFamily: 'Bricolage Grotesque', mt: '20px' }}>{strings.campaign_creation_video_duration_title}</Typography>
          </Grid>
          <Grid item xs={12} mt={2}>
            <ReactHookFormRadioCardGroupIcon name='videoDuration' defaultValue={15} options={videoDurationOptions} />
          </Grid>
        </Fragment>
      )}
      <Grid item xs={12} mt={2}>
        <Typography sx={{ fontSize: '18px', fontWeight: 500, fontFamily: 'Bricolage Grotesque', mt: '20px' }}>{strings.campaign_creation_format_title}</Typography>
      </Grid>
      <Grid item xs={12} mt={2}>
        <ReactHookFormRadioCardGroupIcon name='videoFormat' defaultValue={VideoFormat.vertical} options={videoFormatOptions} />
      </Grid>
    </Grid>
  )
})

export default CampaignType
